import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientPortalLayout from "./ClientPortalLayout";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LocalHospital as HealthcareIcon,
  EventAvailable as AppointmentIcon,
  Description as RecordIcon,
  Payment as BillingIcon,
  Medication as PrescriptionIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";
import { useSnackbar } from "@codejq/react-simple-snackbar";
import { useDispatch } from "react-redux";
import { getHealthcarePortalData } from "../../actions/clientPortalActions";
import { format } from "date-fns";

const HealthcareClientPortal = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation("crm");
  const [openSnackbar] = useSnackbar();
  const dispatch = useDispatch();
  
  // Helper function to normalize language code (e.g., en-US -> en)
  const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    return lang.split('-')[0];
  };
  
  const currentLang = normalizeLanguage(i18n.language);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [records, setRecords] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Fetch healthcare portal data using Redux
  const fetchHealthcareData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dispatch(getHealthcarePortalData());
      setAppointments(data.appointments || []);
      setRecords(data.records || []);
      setPrescriptions(data.prescriptions || []);
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error("Error fetching healthcare data:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchHealthcareData();
  }, [fetchHealthcareData]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
      case 'active':
        return 'success';
      case 'scheduled':
      case 'in progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const modules = [
    {
      name: 'healthcare',
      label: 'Healthcare',
      icon: <HealthcareIcon />,
      path: '/client-portal/healthcare'
    }
  ];

  if (loading) {
    return (
      <ClientPortalLayout modules={modules} currentModule="healthcare">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout modules={modules} currentModule="healthcare">
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          <HealthcareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('healthcare_portal')}
        </Typography>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('overview')} icon={<ReportIcon />} iconPosition="start" />
          <Tab label={t('appointments')} icon={<AppointmentIcon />} iconPosition="start" />
          <Tab label={t('medical_records')} icon={<RecordIcon />} iconPosition="start" />
          <Tab label={t('prescriptions')} icon={<PrescriptionIcon />} iconPosition="start" />
          <Tab label={t('billing')} icon={<BillingIcon />} iconPosition="start" />
        </Tabs>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('upcoming_appointments')}</Typography>
                  <Typography variant="h4">{appointments.filter(a => a.status === 'Scheduled').length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('medical_records')}</Typography>
                  <Typography variant="h4">{records.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('active_prescriptions')}</Typography>
                  <Typography variant="h4">{prescriptions.filter(p => p.status === 'Active').length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('pending_payments')}</Typography>
                  <Typography variant="h4">{invoices.filter(i => i.status === 'Pending').length}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Upcoming Appointments */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>{t('upcoming_appointments')}</Typography>
                <List>
                  {appointments.slice(0, 3).map((appointment) => (
                    <React.Fragment key={appointment._id}>
                      <ListItem>
                        <ListItemText
                          primary={`${appointment.appointmentType} - ${appointment.doctor}`}
                          secondary={`${format(new Date(appointment.date), 'MMM dd, yyyy')} ${t('at')} ${appointment.time} • ${appointment.location}`}
                        />
                        <Chip label={appointment.status} color={getStatusColor(appointment.status)} size="small" />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Appointments Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('appointments')}</Typography>
            {appointments.length === 0 ? (
              <Alert severity="info">{t('no_appointments_scheduled')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('type')}</TableCell>
                      <TableCell>{t('doctor')}</TableCell>
                      <TableCell>{t('date_time')}</TableCell>
                      <TableCell>{t('location')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appointments.map((appointment) => (
                      <TableRow key={appointment._id}>
                        <TableCell>{appointment.appointmentType}</TableCell>
                        <TableCell>{appointment.doctor}</TableCell>
                        <TableCell>
                          {format(new Date(appointment.date), 'MMM dd, yyyy')} {t('at')} {appointment.time}
                        </TableCell>
                        <TableCell>{appointment.location}</TableCell>
                        <TableCell>
                          <Chip label={appointment.status} color={getStatusColor(appointment.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Medical Records Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('medical_records')}</Typography>
            {records.length === 0 ? (
              <Alert severity="info">{t('no_medical_records_available')}</Alert>
            ) : (
              <List>
                {records.map((record) => (
                  <React.Fragment key={record._id}>
                    <ListItem button>
                      <RecordIcon sx={{ mr: 2 }} />
                      <ListItemText
                        primary={record.title}
                        secondary={`${record.recordType} • ${format(new Date(record.date), 'MMM dd, yyyy')} • ${record.doctor} • ${record.summary}`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Prescriptions Tab */}
        {activeTab === 3 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('prescriptions')}</Typography>
            {prescriptions.length === 0 ? (
              <Alert severity="info">{t('no_prescriptions_found')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('medication')}</TableCell>
                      <TableCell>{t('prescribed_by')}</TableCell>
                      <TableCell>{t('dosage')}</TableCell>
                      <TableCell>{t('duration')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescriptions.map((prescription) => (
                      <TableRow key={prescription._id}>
                        <TableCell>{prescription.medicationName}</TableCell>
                        <TableCell>{prescription.prescribedBy}</TableCell>
                        <TableCell>{prescription.dosage}</TableCell>
                        <TableCell>{prescription.duration}</TableCell>
                        <TableCell>
                          <Chip label={prescription.status} color={getStatusColor(prescription.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Billing Tab */}
        {activeTab === 4 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('medical_bills')}</Typography>
            {invoices.length === 0 ? (
              <Alert severity="info">{t('no_billing_information_available')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('invoice_number')}</TableCell>
                      <TableCell>{t('service')}</TableCell>
                      <TableCell>{t('amount')}</TableCell>
                      <TableCell>{t('date')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice._id}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.serviceDescription}</TableCell>
                        <TableCell>${invoice.amount.toLocaleString()}</TableCell>
                        <TableCell>{format(new Date(invoice.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>
    </ClientPortalLayout>
  );
};

export default HealthcareClientPortal;
